import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CompetitionScopeSelector, type CompetitionScope } from './CompetitionScopeSelector';
import { useCompetitionScope } from '@/hooks/useCompetitionScope';
import { 
  Wallet, 
  TrendingUp,
  CreditCard,
  Banknote,
  Target,
  Users,
  Trophy,
  Medal
} from 'lucide-react';

interface DailySales {
  totalAmount: number;
  salesCount: number;
  paymentMethods: {
    cash: number;
    card: number;
    transfer: number;
    credit: number;
  };
  myPersonalSales: {
    amount: number;
    count: number;
    rank: number;
  };
  salesByUser: Array<{
    userId: string;
    userName: string;
    amount: number;
    count: number;
  }>;
}

interface DailySalesKPIProps {
  branchId?: string;
}

export function DailySalesKPI({ branchId }: DailySalesKPIProps) {
  const { selectedCompany, user } = useAuth();
  const { scope, setScope } = useCompetitionScope();
  const [salesData, setSalesData] = useState<DailySales>({
    totalAmount: 0,
    salesCount: 0,
    paymentMethods: { cash: 0, card: 0, transfer: 0, credit: 0 },
    myPersonalSales: { amount: 0, count: 0, rank: 0 },
    salesByUser: []
  });
  const [loading, setLoading] = useState(true);

  const loadDailySales = async () => {
    if (!selectedCompany?.id || !user?.id) return;

    try {
      setLoading(true);
      
      // 1. Obtener sesión activa del usuario para usar su operational_date
      const { data: activeSession } = await supabase
        .from('cash_register_sessions')
        .select('operational_date, session_date')
        .eq('company_id', selectedCompany.id)
        .eq('user_id', user.id)
        .is('closed_at', null)
        .order('opened_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      // 2. Usar operational_date si existe, sino usar fecha actual
      let targetDate: string;
      if (activeSession?.operational_date) {
        targetDate = activeSession.operational_date;
        console.log('📅 Usando fecha operativa simulada:', targetDate);
      } else {
        const now = new Date();
        const localDate = new Date(now.getTime() - (now.getTimezoneOffset() * 60000));
        targetDate = localDate.toISOString().split('T')[0];
        console.log('📅 Usando fecha actual:', targetDate);
      }
      
      // 3. Crear timestamps para inicio y fin del día
      const todayStart = new Date(targetDate + 'T00:00:00');
      const todayEnd = new Date(targetDate + 'T23:59:59.999');

      console.log('Loading daily sales for company:', selectedCompany.id, 'user:', user.id, 'scope:', scope, 'date:', targetDate);

      // Build query based on scope
      let salesQuery;
      if (scope === 'branch' && branchId) {
        // Por sucursal: solo ventas de la sucursal actual
        const { data, error } = await supabase
          .from('sales')
          .select('id, total_amount, payment_method, created_at, cashier_id')
          .eq('company_id', selectedCompany.id)
          .eq('branch_id', branchId)
          .eq('sale_date', targetDate)
          .in('status', ['completed', 'certified']);
        salesQuery = data;
        if (error) throw error;
      } else if (scope === 'commercial_group') {
        // Por grupo comercial: todas las empresas del mismo grupo (requiere investigar estructura)
        // Por ahora, solo empresa actual hasta implementar grupos comerciales
        const { data, error } = await supabase
          .from('sales')
          .select('id, total_amount, payment_method, created_at, cashier_id')
          .eq('company_id', selectedCompany.id)
          .eq('sale_date', targetDate)
          .in('status', ['completed', 'certified']);
        salesQuery = data;
        if (error) throw error;
      } else {
        // Por empresa (default): todas las ventas de la empresa
        const { data, error } = await supabase
          .from('sales')
          .select('id, total_amount, payment_method, created_at, cashier_id')
          .eq('company_id', selectedCompany.id)
          .eq('sale_date', targetDate)
          .in('status', ['completed', 'certified']);
        salesQuery = data;
        if (error) throw error;
      }

      // Remove the old error handling - now handled above

      console.log('Sales query result:', salesQuery);

      // ✅ Obtener pagos individuales para cálculo correcto
      const saleIds = salesQuery?.map(s => s.id) || [];
      let allPayments: Array<{ sale_id: string; payment_method_code: string; amount: number }> = [];
      if (saleIds.length > 0) {
        const { data: paymentsData } = await supabase
          .from('sale_payments')
          .select('sale_id, payment_method_code, amount')
          .in('sale_id', saleIds);
        allPayments = paymentsData || [];
      }

      // 🔒 SECURITY: Obtener perfiles de usuarios filtrados por empresa
      const userIds = [...new Set(salesQuery?.map(s => s.cashier_id).filter(Boolean) || [])] as string[];
      let userProfiles: any[] = [];
      if (userIds.length > 0) {
        const { getProfilesByCompany } = await import('@/utils/profileQueries');
        const { data: profiles } = await getProfilesByCompany(userIds, selectedCompany.id);
        userProfiles = profiles || [];
      }

      // Calculate payment methods and sales by user
      const paymentMethods = {
        cash: 0,
        card: 0,
        transfer: 0,
        credit: 0
      };

      let totalAmount = 0;
      const userSalesMap = new Map<string, { amount: number; count: number; name: string }>();

      // ✅ Calcular métodos de pago desde sale_payments (incluye mixtos)
      allPayments.forEach(p => {
        const amount = Number(p.amount || 0);
        const normalizedMethod = (p.payment_method_code || 'cash').toLowerCase().trim();
        switch (normalizedMethod) {
          case 'cash':
          case 'efectivo':
            paymentMethods.cash += amount;
            break;
          case 'card':
          case 'tarjeta':
          case 'credit_card':
            paymentMethods.card += amount;
            break;
          case 'transfer':
          case 'transferencia':
          case 'bank_transfer':
            paymentMethods.transfer += amount;
            break;
          case 'credit':
          case 'credito':
            paymentMethods.credit += amount;
            break;
          default:
            paymentMethods.cash += amount;
        }
      });

      salesQuery?.forEach(sale => {
        const amount = sale.total_amount || 0;
        totalAmount += amount;

        // Sales by user
        const userId = sale.cashier_id || 'unknown';
        const userProfile = userProfiles.find(p => p.user_id === userId);
        const userName = userProfile?.full_name || userProfile?.email || `Vendedor ${userId.substring(0, 8)}`;
        
        if (userSalesMap.has(userId)) {
          const existing = userSalesMap.get(userId)!;
          userSalesMap.set(userId, {
            amount: existing.amount + amount,
            count: existing.count + 1,
            name: userName
          });
        } else {
          userSalesMap.set(userId, {
            amount: amount,
            count: 1,
            name: userName
          });
        }
      });

      // Convert to array and sort by amount
      const salesByUser = Array.from(userSalesMap.entries()).map(([userId, data]) => ({
        userId,
        userName: data.name,
        amount: data.amount,
        count: data.count
      })).sort((a, b) => b.amount - a.amount);

      // Get current user's personal sales
      // Note: user.id might be different from cashier_id, so we find the best match
      const myPersonalSales = userSalesMap.get(user.id) || { amount: 0, count: 0, name: '' };
      const myRank = salesByUser.findIndex(u => u.userId === user.id) + 1;

      setSalesData({
        totalAmount,
        salesCount: salesQuery?.length || 0,
        paymentMethods,
        myPersonalSales: {
          amount: myPersonalSales.amount,
          count: myPersonalSales.count,
          rank: myRank
        },
        salesByUser: salesByUser.slice(0, 5) // Top 5 sellers
      });

    } catch (error) {
      console.error('Error loading daily sales:', error);
      // Use demo data
      setSalesData({
        totalAmount: 8750.50,
        salesCount: 23,
        paymentMethods: { cash: 4200.00, card: 3250.50, transfer: 1100.00, credit: 200.00 },
        myPersonalSales: { amount: 2150.75, count: 8, rank: 2 },
        salesByUser: [
          { userId: '1', userName: 'María García', amount: 2850.25, count: 12 },
          { userId: user?.id || '2', userName: 'Tú', amount: 2150.75, count: 8 },
          { userId: '3', userName: 'Carlos López', amount: 1950.50, count: 7 },
          { userId: '4', userName: 'Ana Martínez', amount: 1799.00, count: 6 }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDailySales();
    
    // Set up real-time subscription for sales updates
    const channel = supabase
      .channel('sales-competition')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'sales',
          filter: `company_id=eq.${selectedCompany?.id}`
        },
        (payload) => {
          console.log('Nueva venta realizada:', payload);
          // Reload data when new sale is inserted
          loadDailySales();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'sales',
          filter: `company_id=eq.${selectedCompany?.id}`
        },
        (payload) => {
          console.log('Venta actualizada:', payload);
          // Reload data when sale is updated (e.g., status change)
          loadDailySales();
        }
      )
      .subscribe();

    // Optional: Refresh every minute as backup
    const interval = setInterval(loadDailySales, 60 * 1000);
    
    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [selectedCompany?.id, branchId, user?.id, scope]);

  const { formatCurrency } = useTenantConfig();


  if (loading) {
    return (
      <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
        <CardHeader className="pb-4">
          <div className="animate-pulse">
            <div className="h-6 bg-muted/50 rounded w-48 mb-2"></div>
            <div className="h-4 bg-muted/50 rounded w-32"></div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-20 bg-muted/50 rounded"></div>
            <div className="h-16 bg-muted/50 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="apple-card bg-card/60 backdrop-blur-xl border-border/30 shadow-lg">
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center text-lg font-semibold">
          <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center mr-3">
            <Wallet className="h-4 w-4 text-primary" />
          </div>
          Ventas del Día
        </CardTitle>
        <CardDescription className="text-sm text-muted-foreground font-medium">
          {new Date().toLocaleDateString('es-GT', { 
            weekday: 'long', 
            year: 'numeric', 
            month: 'long', 
            day: 'numeric' 
          })}
        </CardDescription>
        
        {/* Competition Scope Selector */}
        <div className="mt-3">
          <CompetitionScopeSelector 
            value={scope} 
            onChange={setScope}
            className="w-full"
          />
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {/* Total Sales */}
        <div className="bg-background/60 backdrop-blur-sm rounded-2xl p-4 border border-border/20">
          <div className="text-center">
            <p className="text-3xl font-bold text-foreground mb-2">
              {formatCurrency(salesData.totalAmount)}
            </p>
            <p className="text-sm text-muted-foreground">
              {salesData.salesCount} ventas realizadas
            </p>
          </div>
        </div>

        {/* Personal Performance vs Team */}
        <div className="bg-background/60 backdrop-blur-sm rounded-2xl p-4 border border-border/20">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Users className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-foreground">Competencia en Vivo</span>
              <div className="w-2 h-2 bg-success rounded-full animate-pulse" title="Actualización en tiempo real"></div>
            </div>
            {salesData.myPersonalSales.rank > 0 && (
              <div className="flex items-center space-x-1">
                {salesData.myPersonalSales.rank === 1 ? (
                  <Trophy className="h-4 w-4 text-warning" />
                ) : salesData.myPersonalSales.rank <= 3 ? (
                  <Medal className="h-4 w-4 text-muted-foreground" />
                ) : null}
                <span className="text-xs font-medium text-muted-foreground">
                  Posición #{salesData.myPersonalSales.rank}
                </span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="text-center">
              <p className="text-lg font-bold text-foreground">
                {formatCurrency(salesData.myPersonalSales.amount)}
              </p>
              <p className="text-xs text-muted-foreground">Mis Ventas</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-foreground">
                {salesData.myPersonalSales.count}
              </p>
              <p className="text-xs text-muted-foreground">Mis Transacciones</p>
            </div>
          </div>

          {/* Top Sellers Ranking */}
          {salesData.salesByUser.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h5 className="text-xs font-medium text-muted-foreground">Ranking del Día</h5>
                <span className="flex items-center text-xs text-status-success">
                  <div className="w-1 h-1 bg-success rounded-full animate-pulse mr-1"></div>
                  En vivo
                </span>
              </div>
              {salesData.salesByUser.map((seller, index) => (
                <div 
                  key={seller.userId} 
                  className={`flex items-center justify-between p-2 rounded-lg transition-all duration-300 ${
                    seller.userId === user?.id 
                      ? 'bg-primary/10 border border-primary/20 shadow-sm' 
                      : 'bg-muted/20 hover:bg-muted/30'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
                      index === 0 ? 'bg-warning text-warning-foreground shadow-lg' :
                      index === 1 ? 'bg-muted text-muted-foreground shadow-md' :
                      index === 2 ? 'bg-destructive/70 text-destructive-foreground shadow-md' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {index + 1}
                    </div>
                    <span className={`text-xs font-medium ${
                      seller.userId === user?.id ? 'text-primary font-semibold' : 'text-foreground'
                    }`}>
                      {seller.userId === user?.id ? 'Tú' : seller.userName}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs font-semibold ${
                      seller.userId === user?.id ? 'text-primary' : 'text-foreground'
                    }`}>
                      {formatCurrency(seller.amount)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {seller.count} ventas
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payment Methods Breakdown */}
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-foreground">Formas de Pago</h4>
          
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-background/60 backdrop-blur-sm rounded-xl p-3 border border-border/20">
              <div className="flex items-center space-x-2 mb-1">
                <Banknote className="h-4 w-4 text-status-success" />
                <span className="text-xs font-medium text-muted-foreground">Efectivo</span>
              </div>
              <p className="text-sm font-bold text-foreground">
                {formatCurrency(salesData.paymentMethods.cash)}
              </p>
              <p className="text-xs text-muted-foreground">
                {salesData.totalAmount > 0 ? ((salesData.paymentMethods.cash / salesData.totalAmount) * 100).toFixed(1) : 0}%
              </p>
            </div>

            <div className="bg-background/60 backdrop-blur-sm rounded-xl p-3 border border-border/20">
              <div className="flex items-center space-x-2 mb-1">
                <CreditCard className="h-4 w-4 text-status-info" />
                <span className="text-xs font-medium text-muted-foreground">Tarjeta</span>
              </div>
              <p className="text-sm font-bold text-foreground">
                {formatCurrency(salesData.paymentMethods.card)}
              </p>
              <p className="text-xs text-muted-foreground">
                {salesData.totalAmount > 0 ? ((salesData.paymentMethods.card / salesData.totalAmount) * 100).toFixed(1) : 0}%
              </p>
            </div>

            <div className="bg-background/60 backdrop-blur-sm rounded-xl p-3 border border-border/20">
              <div className="flex items-center space-x-2 mb-1">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="text-xs font-medium text-muted-foreground">Transferencia</span>
              </div>
              <p className="text-sm font-bold text-foreground">
                {formatCurrency(salesData.paymentMethods.transfer)}
              </p>
              <p className="text-xs text-muted-foreground">
                {salesData.totalAmount > 0 ? ((salesData.paymentMethods.transfer / salesData.totalAmount) * 100).toFixed(1) : 0}%
              </p>
            </div>

            <div className="bg-background/60 backdrop-blur-sm rounded-xl p-3 border border-border/20">
              <div className="flex items-center space-x-2 mb-1">
                <Target className="h-4 w-4 text-warning" />
                <span className="text-xs font-medium text-muted-foreground">Crédito</span>
              </div>
              <p className="text-sm font-bold text-foreground">
                {formatCurrency(salesData.paymentMethods.credit)}
              </p>
              <p className="text-xs text-muted-foreground">
                {salesData.totalAmount > 0 ? ((salesData.paymentMethods.credit / salesData.totalAmount) * 100).toFixed(1) : 0}%
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}